import BaseModel from './BaseModel';
import * as AT from '../../components/container/attributes';
import {required} from '@vuelidate/validators';
import {HitOnlineEnums} from 'hit-online-web-ui/src/helpers/constants';
import {translate} from '../../plugins/i18n/i18n';
import StaffCompensationType from './StaffCompensationType';
import ProjectPart from './ProjectPart';

export default class StaffCompensationValue extends BaseModel {
  constructor({
    id,
    day = undefined,
    staffId = undefined,
    compensationType = undefined,
    quantity = undefined,
    amount = undefined,
    projectPart = undefined,
    source = undefined,
    correctedQuantity = undefined,
    correctedAmount = undefined,
    originalQuantity = undefined,
    originalAmount = undefined,
    correctionComment = undefined,
    correctedBy = undefined,
    correctedAt = undefined,
  }) {
    super(id);
    this.day = day;
    this.staffId = staffId;
    this.compensationType = compensationType
      ? new StaffCompensationType(compensationType)
      : null;
    this.quantity = quantity;
    this.amount = amount;
    this.projectPart = projectPart ? new ProjectPart(projectPart) : null;
    this.source = source
      ? HitOnlineEnums.getEnumValue(
          'STAFF_COMPENSATION_SOURCE',
          source,
          translate,
        )
      : null;
    this.correctedQuantity = correctedQuantity;
    this.correctedAmount = correctedAmount;
    this.originalQuantity = originalQuantity;
    this.originalAmount = originalAmount;
    this.correctionComment = correctionComment;
    this.correctedBy = correctedBy;
    this.correctedAt = correctedAt;
  }

  static get fkName() {
    return null;
  }

  static get apiRoute() {
    return 'staff_compensation_value';
  }

  static get listPageRoute() {
    return null;
  }

  static get detailPageRoute() {
    return null;
  }

  static get createInitColumns() {
    return {
      quantity: 0,
      amount: 0,
    };
  }

  get fkName() {
    return StaffCompensationValue.fkName;
  }

  get apiRoute() {
    return StaffCompensationValue.apiRoute;
  }

  get detailPageRoute() {
    return StaffCompensationValue.detailPageRoute;
  }

  get listPageRoute() {
    return StaffCompensationValue.listPageRoute;
  }

  get createInitColumns() {
    return StaffCompensationValue.createInitColumns;
  }

  static get listDisplayAttrs() {
    return this.getAttributes(
      ['compensationType', 'quantity', 'amount', 'projectPart', 'source'],
      [
        'day',
        'staffId',
        'correctedQuantity',
        'correctedAmount',
        'originalQuantity',
        'originalAmount',
        'correctionComment',
        'correctedBy',
        'correctedAt',
      ],
    );
  }

  static get allAttributes() {
    return [
      new AT.DateAttribute({
        name: 'day',
        validations: {required},
      }),
      new AT.StringAttribute({
        name: 'staffId',
        column: 'staff_id',
        validations: {required},
      }),
      new AT.FkStaffCompensationTypeAttribute({
        name: 'compensationType',
        fkToUpdate: 'staff_compensation_type_id',
        // Avoid full constraint name: it exceeds Postgres' 63-char limit and breaks PostgREST embeds.
        joinPath: ['staff_compensation_type'],
        validations: {required},
        tableProperties: {
          header: 'hit-base.common.compensation',
          maxWidth: '1.5fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'quantity',
        validations: {required},
        decimalPrecision: 4,
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.common.quantity',
          maxWidth: '1fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'amount',
        validations: {required},
        decimalPrecision: 2,
        creationValue: 0,
        tableProperties: {
          header: 'hit-app.common.amount',
          maxWidth: '1fr',
        },
      }),
      new AT.FkProjectPartAttribute({
        name: 'projectPart',
        fkToUpdate: 'project_part_id',
        joinPath: ['project_part'],
        tableProperties: {
          header: 'hit-base.common.project-part',
          maxWidth: '1.5fr',
        },
      }),
      new AT.EnumerationAttribute({
        name: 'source',
        column: 'source',
        enumType: 'STAFF_COMPENSATION_SOURCE',
        enumClass: HitOnlineEnums,
        creationValue: HitOnlineEnums.getEnumValue(
          'STAFF_COMPENSATION_SOURCE',
          'OFFICE',
          translate,
        ),
        tableProperties: {
          header: 'hit-app.staff.hour-source',
          maxWidth: '0.3fr',
          readOnly: true,
        },
      }),
      new AT.DecimalAttribute({
        name: 'correctedQuantity',
        column: 'corrected_quantity',
        decimalPrecision: 4,
        decimalKeepNullValue: true,
        tableProperties: {
          header: 'hit-app.staff.corrected-distance',
          maxWidth: '1fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'correctedAmount',
        column: 'corrected_amount',
        decimalPrecision: 2,
        decimalKeepNullValue: true,
        tableProperties: {
          header: 'hit-app.staff.corrected-amount',
          maxWidth: '1fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'originalQuantity',
        column: 'original_quantity',
        decimalPrecision: 4,
        decimalKeepNullValue: true,
        tableProperties: {
          header: 'hit-app.staff.mobility-original-distance',
          maxWidth: '1fr',
        },
      }),
      new AT.DecimalAttribute({
        name: 'originalAmount',
        column: 'original_amount',
        decimalPrecision: 2,
        decimalKeepNullValue: true,
        tableProperties: {
          header: 'hit-app.staff.mobility-original-amount',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'correctionComment',
        column: 'correction_comment',
        tableProperties: {
          header: 'hit-app.staff.correction-comment',
          maxWidth: '1.2fr',
        },
      }),
      new AT.StringAttribute({
        name: 'correctedBy',
        column: 'corrected_by',
        tableProperties: {
          header: 'hit-app.staff.corrected-by',
          maxWidth: '0.9fr',
          readOnly: true,
        },
      }),
      new AT.DateTimeAttribute({
        name: 'correctedAt',
        column: 'corrected_at',
        tableProperties: {
          header: 'hit-app.staff.corrected-at',
          maxWidth: '1fr',
          readOnly: true,
        },
      }),
    ];
  }
}
